"use client";

import type { MouseEvent } from "react";
import { useStoreLinkEscape } from "@/lib/inAppEscape/InAppEscapeContext";
import { AppleIcon, GooglePlayIcon } from "./icons";

const STORE_BADGE_CLASS = `
  inline-flex h-[52px] items-center gap-3 rounded-xl bg-slate-900 px-5 text-white
  transition-opacity
  hover:opacity-80
  system-dark:bg-white system-dark:text-black
`;

export type RedirectDestinationActionsProps = {
  os: string;
  iosUrl: string | null;
  androidUrl: string | null;
  webUrl: string;
  onStoreTap?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export function RedirectDestinationActions({
  os,
  iosUrl,
  androidUrl,
  webUrl,
  onStoreTap,
}: RedirectDestinationActionsProps) {
  // Undefined unless the visitor is in a browser that refuses to open the store
  // link below, in which case the tap breaks out instead. See lib/inAppEscape.
  const onIosStoreTap = useStoreLinkEscape(iosUrl);

  // Falls through to the web link when the app has no id for this store, rather
  // than offering a badge that lands on a store error page.
  if (os === "ios" && iosUrl) {
    return (
      <a
        href={iosUrl}
        onClick={onStoreTap ?? onIosStoreTap}
        className={STORE_BADGE_CLASS}
      >
        <AppleIcon className="h-7 w-7" />
        <span className="flex flex-col leading-tight">
          <span className="text-[10px] font-normal">Download on the</span>
          <span className="-mt-0.5 text-lg font-semibold">App Store</span>
        </span>
      </a>
    );
  }

  if (os === "android" && androidUrl) {
    return (
      <a href={androidUrl} onClick={onStoreTap} className={STORE_BADGE_CLASS}>
        <GooglePlayIcon className="h-7 w-7" />
        <span className="flex flex-col leading-tight">
          <span className="text-[10px] font-normal">GET IT ON</span>
          <span className="-mt-0.5 text-lg font-semibold">Google Play</span>
        </span>
      </a>
    );
  }

  if (webUrl) {
    return (
      <a href={webUrl} className="text-sm underline">
        Tap here to continue
      </a>
    );
  }

  return (
    <p
      className={`
        text-sm text-slate-500
        system-dark:text-zinc-400
      `}
    >
      No redirect URL was provided for this link
    </p>
  );
}
